import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import CartDrawer        from './components/cart/CartDrawer';
import Nav    from './components/layout/Nav';
import Footer from './components/layout/Footer';

import HomePage             from './pages/HomePage';
import ServicesPage         from './pages/ServicesPage';
import ServiceDetailPage    from './pages/ServiceDetailPage';
import ProgramsPage         from './pages/ProgramsPage';
import ProgramDetailPage    from './pages/ProgramDetailPage';
import AboutPage            from './pages/AboutPage';
import ContactPage          from './pages/ContactPage';
import LoginPage            from './pages/LoginPage';
import DashboardPage        from './pages/DashboardPage';
import CertificatePage      from './pages/CertificatePage';
import { NewsListPage, ArticleDetailPage }           from './pages/NewsPage';
import { InternshipsListPage, InternshipDetailPage } from './pages/InternshipsPage';
import MarketplacePage     from './pages/MarketplacePage';
import AcademyPage         from './pages/AcademyPage';
import ProductDetailPage   from './pages/ProductDetailPage';
import ApplicationPage     from './pages/ApplicationPage';
import CheckoutPage        from './pages/CheckoutPage';

function ScrollReset() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-0)' }}>
      <div style={{ width:32, height:32, border:'2px solid rgba(255,255,255,0.08)', borderTopColor:'var(--gold)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }}/>
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace/>;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace/>;
  return children;
}

function WithLayout({ children }) {
  return (<><Nav/><main>{children}</main><Footer/></>);
}

function WithNav({ children }) {
  return (<><Nav/>{children}</>);
}

function AppRoutes() {
  return (
    <>
      <ScrollReset/>
      <Routes>
        <Route path="/login"               element={<WithNav><PublicOnlyRoute><LoginPage/></PublicOnlyRoute></WithNav>}/>
        <Route path="/dashboard"           element={<PrivateRoute><WithLayout><DashboardPage/></WithLayout></PrivateRoute>}/>
        <Route path="/"                    element={<WithLayout><HomePage/></WithLayout>}/>
        <Route path="/services"            element={<WithLayout><ServicesPage/></WithLayout>}/>
        <Route path="/services/:slug"      element={<WithLayout><ServiceDetailPage/></WithLayout>}/>
        <Route path="/programs"            element={<WithLayout><ProgramsPage/></WithLayout>}/>
        <Route path="/programs/:slug"      element={<WithLayout><ProgramDetailPage/></WithLayout>}/>
        <Route path="/internships"         element={<WithLayout><InternshipsListPage/></WithLayout>}/>
        <Route path="/internships/:slug"   element={<WithLayout><InternshipDetailPage/></WithLayout>}/>
        <Route path="/about"               element={<WithLayout><AboutPage/></WithLayout>}/>
        <Route path="/news"                element={<WithLayout><NewsListPage/></WithLayout>}/>
        <Route path="/news/:slug"          element={<WithLayout><ArticleDetailPage/></WithLayout>}/>
        <Route path="/contact"             element={<WithLayout><ContactPage/></WithLayout>}/>
        <Route path="/verify/:hash"        element={<WithLayout><CertificatePage/></WithLayout>}/>
        <Route path="/verify"             element={<WithLayout><CertificatePage/></WithLayout>}/>

        {/* New nav structure */}
        <Route path="/marketplace"                           element={<WithLayout><MarketplacePage/></WithLayout>}/>
        <Route path="/marketplace/products/:slug"            element={<WithLayout><ProductDetailPage/></WithLayout>}/>
        <Route path="/marketplace/services/:slug"            element={<WithLayout><ServiceDetailPage/></WithLayout>}/>
        <Route path="/academy"                               element={<WithLayout><AcademyPage/></WithLayout>}/>
        <Route path="/academy/programs/:slug"                element={<WithLayout><ProgramDetailPage/></WithLayout>}/>
        <Route path="/academy/programs/:slug/apply"          element={<WithLayout><ApplicationPage/></WithLayout>}/>
        <Route path="/academy/internships/:slug"             element={<WithLayout><InternshipDetailPage/></WithLayout>}/>
        <Route path="/academy/internships/:slug/apply"       element={<WithLayout><ApplicationPage/></WithLayout>}/>

        {/* Cart & Checkout */}
        <Route path="/checkout"                               element={<WithLayout><CheckoutPage/></WithLayout>}/>

        {/* Legacy redirects */}
        <Route path="/services"            element={<Navigate to="/marketplace?tab=services" replace/>}/>
        <Route path="/programs"            element={<Navigate to="/academy?tab=programs" replace/>}/>
        <Route path="/internships"         element={<Navigate to="/academy?tab=internships" replace/>}/>

        <Route path="*"                    element={<WithLayout><HomePage/></WithLayout>}/>
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRoutes/>
          <CartDrawer/>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
